import type { BaseHomeResponse } from "@core/home/types";
import { URLS } from "@core/shared/utils";
import { trans } from "@mongez/localization";
import { Link } from "@remix-run/react";
import { ArrowLeft, Sparkles } from "lucide-react";

type ModernHomeHeroProps = {
  hero?: BaseHomeResponse["hero"];
  isGuest: boolean;
};

export function ModernHomeHero({ hero, isGuest }: ModernHomeHeroProps) {
  return (
    <section className="relative isolate overflow-hidden rounded-[2rem] border border-sky-100 bg-gradient-to-br from-sky-50 via-white to-violet-50 px-6 py-8 shadow-sm dark:border-slate-800 dark:from-slate-900 dark:via-slate-950 dark:to-violet-950 sm:px-10 sm:py-10">
      <div className="absolute -end-16 -top-24 h-64 w-64 rounded-full bg-violet-300/20 blur-3xl" />
      <div className="absolute -bottom-24 -start-20 h-64 w-64 rounded-full bg-cyan-300/20 blur-3xl" />

      <div className="relative grid items-center gap-8 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full border border-violet-200 bg-white/80 px-3 py-1 text-xs font-black text-violet-700 shadow-sm dark:border-violet-800 dark:bg-slate-900/80 dark:text-violet-300">
            <Sparkles className="h-4 w-4" />
            {hero?.badge || trans("home.modernBadge")}
          </span>
          <h1 className="mt-5 max-w-3xl text-3xl font-black leading-tight text-slate-950 dark:text-white sm:text-4xl">
            {hero?.title || trans("home.modernTitle")}
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-600 dark:text-slate-300 sm:text-base">
            {hero?.text || trans("home.modernDescription")}
          </p>
          <Link
            to={isGuest ? URLS.auth.register : URLS.lessons.list}
            className="mt-6 inline-flex items-center gap-2 rounded-xl bg-slate-950 px-5 py-3 text-sm font-black text-white transition hover:-translate-y-0.5 hover:bg-violet-700 dark:bg-white dark:text-slate-950 dark:hover:bg-violet-200">
            {isGuest
              ? trans("home.createAccount")
              : trans("home.exploreLearning")}
            <ArrowLeft className="h-4 w-4 rtl:rotate-0 ltr:rotate-180" />
          </Link>
        </div>

        {hero?.image?.url && (
          <div className="relative hidden aspect-[4/3] overflow-hidden rounded-[1.75rem] border-4 border-white bg-white shadow-xl dark:border-slate-800 lg:block">
            <img
              src={hero.image.url}
              alt={hero.title || trans("home.modernTitle")}
              className="h-full w-full object-cover"
            />
          </div>
        )}
      </div>
    </section>
  );
}
